/**
 * Extract workstream from transaction
 */
export const getWorkstreamFromTransaction = (transaction?: string): string | null => {
  if (!transaction) return null

  const path = transaction.toLowerCase().trim()

  // Check for specific workstreams - order matters! More specific patterns first
  // Check dispatch-mobile BEFORE dispatch (more specific)
  if (path.startsWith('/dispatch-mobile/') || path.includes('/dispatch-mobile/')) {
    return 'dispatch-mobile'
  }

  // Match paths like "/dispatch/", "/operations/ONT/dispatch", "/dispatch", etc.
  if (path.includes('/dispatch') || path.includes('dispatch')) {
    // Make sure it's not dispatch-mobile (already checked above, but double-check)
    if (!path.includes('/dispatch-mobile')) {
      return 'dispatch'
    }
  }

  // Check linehaul - must be /linehaul/ path (be specific to avoid false matches)
  // Match both /linehaul/ and linehaul/ (without leading slash)
  if (path.startsWith('/linehaul/') || path.includes('/linehaul/') || path.startsWith('linehaul/')) {
    return 'linehaul'
  }
  // Check backoffice - can be /back-office/ or /backoffice/
  if (
    path.startsWith('/back-office/') ||
    path.startsWith('/backoffice/') ||
    path.includes('back-office') ||
    path.includes('backoffice')
  ) {
    return 'backoffice'
  }
  // Check inbound - can appear anywhere in path
  if (path.includes('/inbound/') || path.includes('inbound')) {
    return 'inbound'
  }
  // Check outbound - can appear anywhere in path
  if (path.includes('/outbound/') || path.includes('outbound')) {
    return 'outbound'
  }
  return 'others'
}

export type Workstream =
  | 'all'
  | 'linehaul'
  | 'backoffice'
  | 'inbound'
  | 'outbound'
  | 'dispatch'
  | 'dispatch-mobile'
  | 'dock-automation'
  | 'my-daylight'
  | 'others'
  | null

/**
 * Generate Sentry API culprit query pattern for workstream filtering
 *
 * IMPORTANT: The Sentry API requires trailing slashes in culprit patterns
 * to return correct data. Pattern format: culprit:"* /workstream/*"
 *
 * @param workstream - Workstream identifier (linehaul, inbound, outbound, etc.)
 * @returns Culprit query string for API, or null for no filter
 *
 * @example
 * getWorkstreamCulpritQuery('linehaul')
 * // Returns: culprit:"* /linehaul/*"
 *
 * getWorkstreamCulpritQuery('all')
 * // Returns: null (no filtering)
 */
export const getWorkstreamCulpritQuery = (workstream: string | null | undefined): string | null => {
  if (!workstream || workstream === 'all') {
    return null // No filter - let API decide
  }

  // ⚠️ CRITICAL: API requires trailing slashes for correct data
  // Pattern: 'culprit:"*/workstream/*"' NOT 'culprit:"*/workstream*"'
  const workstreamPatterns: Record<string, string> = {
    linehaul: 'culprit:"*/linehaul/*"', // ✅ Trailing slash required
    inbound: 'culprit:"*/inbound/*"', // ✅ Trailing slash required
    outbound: 'culprit:"*/outbound/*"', // ✅ Trailing slash required
    dispatch: 'culprit:"*/dispatch*"', // No slash (matches /dispatch, /dispatch-mobile, etc.)
    backoffice: 'culprit:"*/back-office/*"', // ✅ Trailing slash required
    others:
      '-culprit:"*/linehaul/*" -culprit:"*/inbound/*" -culprit:"*/outbound/*" -culprit:"*/dispatch*" -culprit:"*/back-office/*"',
  }

  return workstreamPatterns[workstream.toLowerCase()] || null
}
